import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { FaIconSize } from '@erikdakoda/ui/components/FaIconUvilo';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { restoreItems } from '@erikdakoda/ui/shared/restoreItems';
import { useUpdateManyTodoItem } from '@erikdakoda/database/hooks/todo-item';
import type { TodoState } from '@erikdakoda/database/enums';
import { TODO_STATE_META, TODO_STATE_ORDER } from '../types';
import TodoStateIcon from './TodoStateIcon';

type TodoStateControlProps = {
  todoId: string;
  state: TodoState;
  onChange: (state: TodoState) => void;
  /** Called after the todo is trashed (e.g. clear selection or close a panel). */
  onTrashed?: () => void;
  /** Called after undo restores the todo (e.g. refetch a grid). */
  onRestored?: () => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

const ICON_SIZE_BY_CONTROL_SIZE: Record<'small' | 'medium', FaIconSize> = {
  small: 'md',
  medium: 'lg',
};

/**
 * Icon button that opens a menu of todo states and a trash action with undo.
 */
export default function TodoStateControl({
  todoId,
  state,
  onChange,
  onTrashed,
  onRestored,
  disabled = false,
  size = 'small',
}: TodoStateControlProps) {
  'use memo';

  const { t } = useTranslation('todo-ui');
  const { snack } = useSnack();
  const exception = useException();
  const updateManyTodoItem = useUpdateManyTodoItem().mutateAsync;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const iconSize = ICON_SIZE_BY_CONTROL_SIZE[size];

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (nextState: TodoState) => {
    handleClose();
    if (nextState !== state) {
      onChange(nextState);
    }
  };

  async function handleTrash(): Promise<void> {
    handleClose();
    try {
      // updateMany avoids ZenStack read-back denial on the newly trashed row
      // (`@@deny('read', trashedAt != null)`).
      const { count } = await updateManyTodoItem({
        where: { id: todoId },
        data: { trashedAt: new Date() },
      });
      if (count === 0) {
        throw new Error('Todo item not found');
      }
      const undo = async () => {
        await restoreItems('TodoItem', [todoId]);
        onRestored?.();
      };
      snack.success(t('todoDeleted'), { undo });
      onTrashed?.();
    } catch (err: unknown) {
      exception(err);
    }
  }

  return (
    <>
      <IconButton
        className="todo-state-control"
        size={size}
        onClick={handleOpen}
        disabled={disabled}
        aria-label={`Todo state: ${TODO_STATE_META[state].label}`}
        sx={{ p: 0.5 }}
      >
        <TodoStateIcon state={state} size={iconSize} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        {TODO_STATE_ORDER.map((option) => {
          const meta = TODO_STATE_META[option];
          return (
            <MenuItem key={option} selected={option === state} onClick={() => handleSelect(option)}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TodoStateIcon state={option} />
              </ListItemIcon>
              <ListItemText>{meta.label}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem onClick={() => void handleTrash()}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FaIconUvilo icon="trash-can" />
          </ListItemIcon>
          <ListItemText>{t('trashTodo')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
